'use strict';

'use strict';

import React, {
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {buildDataDescriptor} from '~/shared/src/utilities/dataStructure';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';

import DataTable,
{
    AllProps as DataTableAllProps,
    componentProps as dataTableComponentProps,
    ComponentProps as DataTableComponentProps,
    defaultProps as dataTableDefaultProps,
    DefaultProps as DataTableDefaultProps,
    Props as DataTableProps
} from '~/frontend/src/components/base/DataTable';
import Toolbar, {
    ComponentProps as ToolbarComponentProps,
    componentProps as toolbarComponentProps
} from '~/frontend/src/components/base/ItemFinder/history/toolbar';

import {getColumnConfigFromColumnDefinition} from '~/frontend/src/components/base/DataTable/utilities';
import {extractViewPropsFromProps} from '~/frontend/src/components/base/ItemFinder/overview/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import loadDataFromBE from '~/frontend/src/actions/data/loadData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';


export type DefaultProps = DataTableDefaultProps & {
    dataContext? : string;
};
export const defaultProps : Required<DefaultProps> = {
    ...dataTableDefaultProps,
    dataContext: 'itemFinderItemHistory'
};

export type Props = Omit<DataTableProps, 'dataContext' | 'reload'> & {
    schema : Schema;
    rootItemId : number;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


type ElusiveData = PanelElusiveDataState;


const ItemHistory = ( props : AllProps ) => {
    const dispatch : AppDispatch   = useDispatch(),
          {
              uid,
              rootItemId,
              schema,
              dataContext
          }                        = props,
          user                     = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          elusiveData              = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          xhr                      = useSelector( ( state : StoreState ) => state.xhr[ uid ] ),
          messageCatalogTimestamp  = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          language                 = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          isLoading                = useMemo(
              () => {
                  return !!xhr?.[ 'loadItemHistoryData' ];
              },
              [
                  xhr
              ]
          ),
          dataStructure            = useMemo(
              () => {
                  return getDataStructureForSqlQuery();
              },
              [
                  'onlyOnce'
              ]
          ),
          defaultColumnDefinitions = useMemo(
              () => {
                  return [
                      {
                          key           : 'id',
                          field         : 'id',
                          header        : __( 'YN.base.common.attribute.id' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'id' }, dataStructure ),
                              valueType: 'INTEGER'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'key',
                          field         : 'key',
                          header        : __( 'YN.base.finder.item.keyLabel' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'key' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 350
                      },
                      {
                          key           : 'name',
                          field         : 'name',
                          header        : __( 'YN.base.finder.item.nameLabel' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'name' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 350
                      },
                      {
                          key           : 'statusId',
                          field         : 'statusId',
                          header        : __( 'YN.base.common.attribute.statusId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'statusId' }, dataStructure ),
                              valueType: 'STATUSID'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'modifierUserId',
                          field         : 'modifierUserId',
                          header        : __( 'YN.base.common.attribute.modifierUserId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'modifierUserId' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 150
                      },
                      {
                          key           : 'validFromTime',
                          field         : 'validFromTime',
                          header        : __( 'YN.base.common.attribute.validFromTime' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'validFromTime' }, dataStructure ),
                              valueType: 'TIMESTAMP'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'validToTime',
                          field         : 'validToTime',
                          header        : __( 'YN.base.common.attribute.validToTime' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'validToTime' }, dataStructure ),
                              valueType: 'TIMESTAMP'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      }
                  ] as ColumnDefinition[];
              },
              [
                  dataStructure,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          defaultColumns           = useMemo(
              () => {
                  return defaultColumnDefinitions.map( iCD => getColumnConfigFromColumnDefinition( iCD ) ) as ColumnConfig[];
              },
              [
                  defaultColumnDefinitions
              ]
          ),
          defaultSorters           = useMemo(
              () => {
                  return [
                      { field: 'validFromTime', direction: 'DESC' }
                  ] as ColumnSorter[];
              },
              [
                  'onlyOnce'
              ]
          ),
          loadData                 = useCallback(
              ( dataTableProps : DataTableAllProps ) => {
                  const params = {
                            schema: schema,
                            itemId: rootItemId,

                            filters: dataTableProps.filters,
                            sorters: dataTableProps.sorters,

                            start: ( dataTableProps.page - 1 ) * dataTableProps.rows,
                            limit: dataTableProps.rows
                        },
                        doLoad = async () => {
                            try {
                                await dispatch( loadDataFromBE(
                                    'finder/loadItemHistoryData',
                                    dataContext,
                                    uid,
                                    'loadItemHistoryData',
                                    schema,
                                    {
                                        params,
                                        extraMetaDataKeys: [ 'rowData', 'rowDataMapping', 'total' ]
                                    }
                                ) );
                            }
                            catch ( e ) {
                                console.error( e );
                            }
                        };

                  void doLoad();

                  return true;
              },
              [
                  rootItemId,
                  schema,
                  uid
              ]
          );

    return <>
        <Toolbar
            {..._.pick( props, toolbarComponentProps ) as ToolbarComponentProps}

            key={`${uid}_toolbar`}

            dataStructure={dataStructure}
            columnDefinitions={defaultColumnDefinitions}
            columns={Object.keys( props.columns ).length ? props.columns : defaultColumns}
            sorters={Object.keys( props.sorters ).length ? props.sorters : defaultSorters}

            exportLoadDataUrl="finder/loadItemHistoryData"
            viewSelectorPanelProps={{ ...extractViewPropsFromProps( props as unknown as DataTableAllProps ) }}

            className="yn-panel-toolbar-border-none-bottom"
        />
        <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
            <DataTable
                {..._.pick( props, dataTableComponentProps ) as DataTableComponentProps}

                key={uid}

                dataStructure={dataStructure}
                columnDefinitions={defaultColumnDefinitions}
                columns={Object.keys( props.columns ).length ? props.columns : defaultColumns}
                sorters={Object.keys( props.sorters ).length ? props.sorters : defaultSorters}

                search={true}
                loading={isLoading}
                reload={loadData}

                value={elusiveData?.metaData?.rowData}
                totalRecords={elusiveData?.metaData?.total}
                valueFieldMapping={elusiveData?.metaData?.rowDataMapping}

                className="yn-flex yn-finder yn-datatable-border-none-top"
            />
        </div>
    </>;
};
ItemHistory.defaultProps = defaultProps;
ItemHistory.displayName = 'FinderHistory-Item';

export default ItemHistory;
